// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, type IntlShape } from 'react-intl';
import { formValueSelector } from 'redux-form';

import type { Dispatch, FieldDefinition } from 'state/types';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import { CreateFieldForm } from './FieldForm';
import { CREATE_FIELD_FORM, CREATE_FIELD_PANEL_ID } from 'util/constants';
import localizedStrings from 'util/localizedStrings';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import {
  getActivePanelId,
  getFieldNames,
  getSelectedFieldCategory,
} from 'state/selectors';
import { actions as dialogActions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
import { actions as catalogActions } from 'state/reducers/catalogReducer';
import { addFields } from 'state/commands/catalogCommands';
import {
  FIELDS_CATEGORY_ALL,
  FIELDS_CATEGORY_CUSTOM,
} from 'util/fieldsPageUtils';
import { FIELD_TYPE } from 'portfolio-shared/util/fieldDefUtils';

const { hideDialogPanel } = dialogActions;
const {
  setSelectedFieldCategory,
  setSelectedFieldsListItemName,
} = catalogActions;

type Props = {
  activePanelId: ?string,
  addFields: Function,
  catalogId: string,
  field: Object,
  allFieldNames: string[],
  hideDialogPanel: Function,
  intl: IntlShape,
  selectedFieldCategory: string,
  setSelectedFieldCategory: Function,
  setSelectedFieldsListItemName: Function,
  systemName: ?string,
};

type State = {
  field: FieldDefinition,
};

const initialFieldState: FieldDefinition = {
  defaultValues: [],
  hasPredefinedValues: false,
  id: '',
  isEmbeddable: false,
  isExtractable: false,
  isHierarchical: false,
  isMultivalued: false,
  isSearchable: false,
  isSortable: false,
  isSystem: false,
  maxLength: 250,
  nestedPredefinedValues: [],
  precision: 2,
  predefinedValues: [],
  showTime: false,
  systemName: '',
  type: 0,
  usePredefinedOnly: false,
};

class CreateFieldDialog extends Component<Props, State> {
  handleCreateField: Function;
  isOkEnabled: Function;

  state = { field: initialFieldState };

  constructor() {
    super();
    this.isOkEnabled = this.isOkEnabled.bind(this);
    this.handleCreateField = this.handleCreateField.bind(this);
  }

  isOkEnabled() {
    const { systemName } = this.props.field;
    const { allFieldNames } = this.props;
    const trimmedFieldName = systemName && systemName.trim();
    return (
      trimmedFieldName &&
      !allFieldNames.find(
        old => old.toLowerCase() === trimmedFieldName.toLowerCase()
      )
    );
  }

  async handleCreateField() {
    const { addFields, catalogId, field, selectedFieldCategory } = this.props;
    if (field.type === FIELD_TYPE.URL) {
      field.isMultivalued = true;
    }
    if (field.type === FIELD_TYPE.TEXTBLOCK) {
      field.maxLength = undefined;
      field.precision = undefined;
    }
    await addFields(catalogId, [field]);
    // new field won't be visible unless we're already in All or Custom category
    if (
      selectedFieldCategory !== FIELDS_CATEGORY_ALL &&
      selectedFieldCategory !== FIELDS_CATEGORY_CUSTOM
    ) {
      this.props.setSelectedFieldCategory(FIELDS_CATEGORY_ALL);
    }
    this.props.setSelectedFieldsListItemName(field.systemName);
  }

  render() {
    const {
      activePanelId,
      field,
      hideDialogPanel,
      intl: { formatMessage },
    } = this.props;

    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '650px', height: null }}
        customButtonText={formatMessage(localizedStrings.CREATE)}
        dialogFocusId="systemName_create"
        hideDialogPanel={hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onClose={() => {}}
        onSave={this.handleCreateField}
        panelId={CREATE_FIELD_PANEL_ID}
        panelTitle={formatMessage(localizedStrings.FIELD_ACTION_ADD)}
        showCancel
        dataTestName="create-field-dialog"
      >
        <CreateFieldForm field={{ ...this.state.field, ...field }} />
      </DialogPanel>
    );
  }
}

const formSelector = formValueSelector(CREATE_FIELD_FORM);

function formDefaultValueToFieldDefaultValues(v: ?string): Array<string> {
  return v ? [v] : [];
}

function toNumber(n) {
  if (!n) {
    return 0;
  }
  return typeof n === 'number' ? n : Number(n);
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    allFieldNames: getFieldNames(state),
    field: {
      systemName: formSelector(state, 'systemName'),
      type: toNumber(formSelector(state, 'type')),
      hasPredefinedValues: formSelector(state, 'hasPredefinedValues'),
      isMultivalued: formSelector(state, 'isMultivalued'),
      maxLength: formSelector(state, 'maxLength'),
      precision: formSelector(state, 'precision'),
      showTime: formSelector(state, 'showTime'),
      usePredefinedOnly: formSelector(state, 'usePredefinedOnly'),
      predefinedValues: formSelector(state, 'predefinedValues'),
      defaultValues: formDefaultValueToFieldDefaultValues(
        formSelector(state, 'defaultValue')
      ),
    },
    selectedFieldCategory: getSelectedFieldCategory(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      addFields,
      hideDialogPanel,
      setSelectedFieldCategory,
      setSelectedFieldsListItemName,
    })
)(injectIntl(CreateFieldDialog));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CreateFieldDialog.js